/**
 * ============================================================================
 * OEE 模块 API 工厂函数
 * ============================================================================
 * 
 * 📋 文件职责：
 * - 定义 OEE（设备综合效率）模块的所有 API 请求方法
 * - 通过工厂模式实现请求客户端的依赖注入
 * - 提供完整的 TypeScript 类型安全
 * 
 * 🎯 核心概念：工厂模式
 * - 工厂函数：createOeeApi(client) - 接收请求客户端，返回 API 方法集合
 * - 依赖注入：不依赖具体的请求实现（axios、uni.request 等）
 * - 平台无关：Web 端和 App 端都可以使用，只需提供不同的适配器
 * 
 * @module api/oee/factory
 * @description OEE（设备综合效率）模块的 API 工厂函数
 */

// ============================================================================
// 类型导入
// ============================================================================

import type {
  // 响应类型
  OeeOverview,
  OeeEquipmentItem,
  OeeTrendItem,
  OeeLossesAnalysis,
  OeeRecord,
  OeeDowntimeReason,
  OeeDowntimeRecord,
  OeePlannedTimeConfig,
  OeeCycleTimeConfig,
  OeeBatchCalculateResult,
  // 请求类型
  CreateOeeDowntimeReasonForm,
  UpdateOeeDowntimeReasonForm,
  CreateOeeDowntimeRecordForm,
  UpdateOeeDowntimeRecordForm,
  VerifyOeeDowntimeRecordForm,
  CreateOeePlannedTimeConfigForm,
  UpdateOeePlannedTimeConfigForm,
  CreateOeeCycleTimeConfigForm,
  UpdateOeeCycleTimeConfigForm,
  CalculateOeeForm,
  // 查询参数
  OeeOverviewQueryParams,
  OeeEquipmentListQueryParams,
  OeeTrendQueryParams,
  OeeLossesQueryParams,
  OeeDowntimeReasonQueryParams,
  OeeDowntimeRecordQueryParams,
  OeePlannedTimeConfigQueryParams,
  OeeCycleTimeConfigQueryParams,
} from '../../types/oee'

import type { PaginationResult } from '../../types/api'
import type { IRequestClient } from '../core/client'
import { API_ROUTES } from '../routes'

// ============================================================================
// OEE API 工厂函数
// ============================================================================

/**
 * 创建 OEE 模块的 API 方法集合
 * 
 * @example
 * ```typescript
 * // Web 项目
 * import { createOeeApi } from '@bayes-mes/shared/api/oee/factory'
 * import { webAdapter } from './adapters/http'
 * 
 * export const oeeApi = createOeeApi(webAdapter)
 * 
 * // 使用
 * const overview = await oeeApi.getOverview({ workshop_id: 1 })
 * const equipments = await oeeApi.getEquipmentList({ date: '2025-01-01' })
 * ```
 */
export function createOeeApi(client: IRequestClient) {
  return {
    // ========================================
    // 看板数据接口
    // ========================================

    /**
     * 获取车间 OEE 概览
     */
    getOverview(params?: OeeOverviewQueryParams) {
      return client.get<OeeOverview>(
        API_ROUTES.oee.dashboardOverview,
        { params }
      )
    },

    /**
     * 获取设备 OEE 列表
     */
    getEquipmentList(params?: OeeEquipmentListQueryParams) {
      return client.get<{ items: OeeEquipmentItem[]; total: number }>(
        API_ROUTES.oee.dashboardEquipments,
        { params }
      )
    },

    /**
     * 获取单设备实时 OEE
     */
    getEquipmentRealtime(equipmentId: number, params?: { date?: string }) {
      return client.get<OeeRecord>(
        API_ROUTES.oee.dashboardEquipmentRealtime(equipmentId),
        { params }
      )
    },

    /**
     * 获取 OEE 趋势
     */
    getTrend(params?: OeeTrendQueryParams) {
      return client.get<{ items: OeeTrendItem[] }>(
        API_ROUTES.oee.trend,
        { params }
      )
    },

    /**
     * 获取六大损失分析
     */
    getLosses(params?: OeeLossesQueryParams) {
      return client.get<OeeLossesAnalysis>(
        API_ROUTES.oee.dashboardLosses,
        { params }
      )
    },

    /**
     * 手动触发 OEE 计算
     */
    calculate(data?: CalculateOeeForm) {
      return client.post<OeeBatchCalculateResult | { equipment_id: number; oee: number }>(
        API_ROUTES.oee.calculate,
        data
      )
    },

    // ========================================
    // 停机原因配置接口
    // ========================================

    /**
     * 获取停机原因列表
     */
    getDowntimeReasonList(params?: OeeDowntimeReasonQueryParams) {
      return client.get<PaginationResult<OeeDowntimeReason>>(
        API_ROUTES.oee.downtimeReasons,
        { params }
      )
    },

    /**
     * 获取停机原因详情
     */
    getDowntimeReasonDetail(id: number) {
      return client.get<OeeDowntimeReason>(
        API_ROUTES.oee.configDowntimeReasons(id)
      )
    },

    /**
     * 创建停机原因
     */
    createDowntimeReason(data: CreateOeeDowntimeReasonForm) {
      return client.post<OeeDowntimeReason>(
        API_ROUTES.oee.downtimeReasons,
        data
      )
    },

    /**
     * 更新停机原因
     */
    updateDowntimeReason(id: number, data: UpdateOeeDowntimeReasonForm) {
      return client.put<OeeDowntimeReason>(
        API_ROUTES.oee.configDowntimeReasons(id),
        data
      )
    },

    /**
     * 删除停机原因
     */
    deleteDowntimeReason(id: number) {
      return client.delete<void>(
        API_ROUTES.oee.delete(id)
      )
    },

    // ========================================
    // 停机记录接口
    // ========================================

    /**
     * 获取停机记录列表
     */
    getDowntimeRecordList(params?: OeeDowntimeRecordQueryParams) {
      return client.get<PaginationResult<OeeDowntimeRecord>>(
        API_ROUTES.oee.downtime,
        { params }
      )
    },

    /**
     * 获取停机记录详情
     */
    getDowntimeRecordDetail(id: number) {
      return client.get<OeeDowntimeRecord>(
        API_ROUTES.oee.oeeDowntime(id)
      )
    },

    /**
     * 创建停机记录（手动录入）
     */
    createDowntimeRecord(data: CreateOeeDowntimeRecordForm) {
      return client.post<OeeDowntimeRecord>(
        API_ROUTES.oee.downtime,
        data
      )
    },

    /**
     * 更新停机记录
     */
    updateDowntimeRecord(id: number, data: UpdateOeeDowntimeRecordForm) {
      return client.put<OeeDowntimeRecord>(
        API_ROUTES.oee.oeeDowntime(id),
        data
      )
    },

    /**
     * 核实停机记录
     */
    verifyDowntimeRecord(id: number, data?: VerifyOeeDowntimeRecordForm) {
      return client.post<OeeDowntimeRecord>(
        API_ROUTES.oee.verify(id),
        data
      )
    },

    // ========================================
    // 计划时间配置接口
    // ========================================

    /**
     * 获取计划时间配置列表
     */
    getPlannedTimeConfigList(params?: OeePlannedTimeConfigQueryParams) {
      return client.get<PaginationResult<OeePlannedTimeConfig>>(
        API_ROUTES.oee.plannedTime,
        { params }
      )
    },

    /**
     * 获取计划时间配置详情
     */
    getPlannedTimeConfigDetail(id: number) {
      return client.get<OeePlannedTimeConfig>(
        API_ROUTES.oee.configPlannedTime(id)
      )
    },

    /**
     * 创建计划时间配置
     */
    createPlannedTimeConfig(data: CreateOeePlannedTimeConfigForm) {
      return client.post<OeePlannedTimeConfig>(
        API_ROUTES.oee.plannedTime,
        data
      )
    },

    /**
     * 更新计划时间配置
     */
    updatePlannedTimeConfig(id: number, data: UpdateOeePlannedTimeConfigForm) {
      return client.put<OeePlannedTimeConfig>(
        API_ROUTES.oee.configPlannedTime(id),
        data
      )
    },

    /**
     * 删除计划时间配置
     */
    deletePlannedTimeConfig(id: number) {
      return client.delete<void>(
        API_ROUTES.oee.configPlannedTime(id)
      )
    },

    // ========================================
    // 理想周期时间配置接口
    // ========================================

    /**
     * 获取理想周期时间配置列表
     */
    getCycleTimeConfigList(params?: OeeCycleTimeConfigQueryParams) {
      return client.get<PaginationResult<OeeCycleTimeConfig>>(
        API_ROUTES.oee.cycleTime,
        { params }
      )
    },

    /**
     * 获取理想周期时间配置详情
     */
    getCycleTimeConfigDetail(id: number) {
      return client.get<OeeCycleTimeConfig>(
        API_ROUTES.oee.configCycleTime(id)
      )
    },

    /**
     * 创建理想周期时间配置
     */
    createCycleTimeConfig(data: CreateOeeCycleTimeConfigForm) {
      return client.post<OeeCycleTimeConfig>(
        API_ROUTES.oee.cycleTime,
        data
      )
    },

    /**
     * 更新理想周期时间配置
     */
    updateCycleTimeConfig(id: number, data: UpdateOeeCycleTimeConfigForm) {
      return client.put<OeeCycleTimeConfig>(
        API_ROUTES.oee.configCycleTime(id),
        data
      )
    },

    /**
     * 删除理想周期时间配置
     */
    deleteCycleTimeConfig(id: number) {
      return client.delete<void>(
        API_ROUTES.oee.configCycleTime(id)
      )
    },

    // ========================================
    // OEE 记录查询接口
    // ========================================

  }
}

/**
 * OEE API 类型（用于类型推导）
 */
export type OeeApi = ReturnType<typeof createOeeApi>
